import { useQuery } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import {
  formatMoney,
  SALES_DOCUMENT_STATUS_LABELS,
  SALES_DOCUMENT_STATUS_TONE,
  SALES_DOCUMENT_TYPE_LABELS,
} from "@finora/config";
import { EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

/**
 * "Moji računi" - vidi razgovor "kupci bi trebali da imaju Deo Moj računi
 * u meniju". Prikazuje ponude/fakture koje je DRUGA Finora firma (najčešće
 * matična, preko "Moj paket" -> "Promeni paket") izdala OVOJ firmi - ti
 * dokumenti žive u tuđem tenant-u, pa se ovde vide samo za čitanje. Klikom
 * se otvara javna stranica dokumenta (ista koju bi dobio i svaki drugi
 * kupac emailom) gde se dokument može i platiti.
 */
export function MyInvoicesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-invoices"],
    queryFn: () => api.getMyInvoices(),
  });

  const documents = data?.documents ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Moji računi</h1>
        <p className="text-sm text-slate-500">
          {data?.issuerCompanyName
            ? `Ponude i fakture koje Vam izdaje ${data.issuerCompanyName}.`
            : "Ponude i fakture koje Vam izdaje Vaš dobavljač HAFiskal usluge."}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : documents.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
          <EmptyState
            icon={Receipt}
            title="Nema računa"
            description="Ovde će se pojaviti ponude i fakture koje Vam izda Vaš dobavljač HAFiskal usluge (npr. pri promeni paketa)."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-app-bg text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="p-3 text-left">Broj</th>
                <th className="p-3 text-left">Tip</th>
                <th className="p-3 text-left">Datum</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Ukupno</th>
                <th className="p-3 text-right">Za uplatu</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => {
                const href = doc.publicLinkToken
                  ? doc.type === "INVOICE"
                    ? `/pregled/racun/${doc.publicLinkToken}`
                    : `/pregled/ponuda/${doc.publicLinkToken}`
                  : null;
                return (
                  <tr key={doc.id} className="border-t border-border">
                    <td className="p-3 font-medium text-slate-800">{doc.documentNumber ?? "—"}</td>
                    <td className="p-3 text-slate-500">{SALES_DOCUMENT_TYPE_LABELS[doc.type]}</td>
                    <td className="p-3 text-slate-500">
                      {new Date(doc.issueDate).toLocaleDateString("sr-RS")}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        label={SALES_DOCUMENT_STATUS_LABELS[doc.status]}
                        tone={SALES_DOCUMENT_STATUS_TONE[doc.status]}
                      />
                    </td>
                    <td className="p-3 text-right text-slate-700">{formatMoney(doc.grossAmount)}</td>
                    <td className="p-3 text-right font-medium text-slate-900">
                      {formatMoney(doc.dueAmount)}
                    </td>
                    <td className="p-3 text-right">
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-brand hover:underline"
                        >
                          Pregledaj
                        </a>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
